<template>
  <div>
    <div class="text-center mb-5">
      <h1>Bienvenue à Dlibr</h1>
      <h3 class="text-primary">Accès d'invité</h3>
      <h2 class="lead h4 text-primary mb-3">
        Pour participer à la délibération, les invités doivent s'identifier.
      </h2>
      <b-btn
        class="d-block"
        size="sm"
        :to="{ name: 'auth.login.options' }"
        variant="link"
      >
        Participer en tant qu'étudiant ou enseignant
      </b-btn>
    </div>
    <b-form @submit.stop.prevent="onSubmit">
      <b-form-group id="nickname" label="Votre nom" label-for="nickname-input">
        <b-form-input
          id="nickname-input"
          name="nickname-input"
          @change="setValue('nickname', $event)"
          :value="form.nickname"
          :state="validateState('nickname')"
          aria-describedby="input-nickname-live-feedback"
        />
        <b-form-invalid-feedback id="input-nickname-live-feedback">
          Ce champ doit comporter au moins 5 caractères.
        </b-form-invalid-feedback>
      </b-form-group>

      <div class="d-flex justify-content-end align-items-center">
        <b-form-checkbox
          class="px-3"
          size="sm"
          id="signed_contract"
          v-model="form.signed_contract"
          name="signed_contract"
        >
          J'accepte les
          <b-link target="_blank" :to="{ name: 'auth.conditions' }"
            >conditions d'utilisation.</b-link
          >
        </b-form-checkbox>
        <b-btn
          pill
          :disabled="authStatus === 'fetching' || !form.signed_contract"
          class="px-4"
          type="submit"
          variant="primary"
        >
          <span>Me joindre à la délibération</span>
          <b-spinner class="ml-2" small v-if="authStatus == 'fetching'"
        /></b-btn>
      </div>
    </b-form>
  </div>
</template>

<script>
/**
 * @file       Login username component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */
import { required, minLength, helpers } from "vuelidate/lib/validators";
import { mapActions, mapState } from "vuex";
import { authMixin } from "@/modules/auth/mixins";

// Alpha-numeric regex + accents
const alphaNumFrench = helpers.regex(
  "alphaNumAndDot",
  /^[a-zàâçéèêëîïôûùüÿñæœ .-]*$/i
);

/**
 * Export component.
 * 
 * @type  {Object}
 */
export default {
  name: "pseudoLogin",
  mixins: [authMixin],

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      form: {
        nickname: null,
        signed_contract: false,
        role: "guest"
      }
    };
  },

  /**
   * Vuelidate form validations.
   * 
   * @type  {Object}
   */
  validations: {
    form: {
      nickname: {
        required,
        alphaNumFrench,
        minLength: minLength(5)
      }
    }
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      authStatus: state => state.auth.profile.status
    })
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    ...mapActions("auth", {
      login: "login"
    }),

    /**
     * Sets the form value.
     *
     * @param      {string}  key     The new value
     * @param      {string}  value   The value
     */
    setValue(key, value) {
      this.form[key] = value;
      this.$v.form[key].$touch();
    },

    /**
     * Validate the form state.
     *
     * @param      {string}  name    The input name
     *
     * @return     {bool}  Dirty or not.
     */
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    /**
     * Called on submit.
     */
    onSubmit() {
      this.$v.form.$touch();

      // If any error found, stop the submit
      if (this.$v.form.$anyError || !this.form.signed_contract) {
        return;
      }

      // Sends the on login request
      this.onLogin(this.form);
    }
  }
};
</script>
